import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { colorsForDepartment } from './departmentColors';

const initialsOf = (firstName, lastName) => {
  const first = (firstName || '').trim()[0] || '';
  const last = (lastName || '').trim()[0] || '';
  return `${first}${last}`.toUpperCase();
};

/**
 * OrgNode - a single card in the org chart tree.
 *
 * Two real `<Link>`s point at the same employee page - the visible name
 * (the one real, tab-reachable, screen-reader-announced stop) and an
 * invisible one stretched over the whole card (`aria-hidden`, `tabIndex={-1}`)
 * so mouse/touch users can click anywhere on the card, not just the name.
 * Because both go to the identical href, it never matters which one actually
 * receives a given click - there is no click handler here to double-fire.
 * The collapse toggle is a real, separately focusable `<button>` layered
 * above both via z-index, so it is never shadowed by the stretched link.
 */
const OrgNode = ({
  node,
  isMe = false,
  isCollapsed = false,
  onToggle,
  highlighted = false,
  dimmed = false,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const colors = colorsForDepartment(node.department_id);
  const employeeHref = `/employees/${node.id}`;

  const handleToggleClick = (event) => {
    event.stopPropagation();
    if (onToggle) onToggle(node.id);
  };

  // Native <button> activation-by-keyboard is a real-browser default action
  // that jsdom does not simulate, so this is spelled out explicitly rather
  // than relied on - stopPropagation here matters for the same reason it
  // matters on click: without it, Enter/Space would still reach a keydown
  // handler on an ancestor (there is none any more, but this keeps the two
  // handlers symmetric and the guarantee explicit rather than incidental).
  const handleToggleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      if (onToggle) onToggle(node.id);
    }
  };

  return (
    // Plain div, not role="button": the collapse toggle below is a real,
    // separately focusable <button>, and role="button" + tabIndex here would
    // make this a second, outer "button" wrapping it - a nested-interactive
    // a11y violation. Card-wide "click navigates" now comes from the
    // stretched Link below, not a click handler on this div.
    <div
      data-testid={`org-node-${node.id}`}
      data-highlighted={highlighted || undefined}
      data-dimmed={dimmed || undefined}
      className={twMerge(
        'org-node relative w-[196px] rounded-lg border bg-white p-3',
        node.hidden_report_count > 0 ? 'pb-4' : 'pb-2.5',
        'text-left shadow-sm transition hover:border-primary-400 hover:shadow-md',
        isMe ? 'border-2 border-warning-500 bg-warning-50' : 'border-gray-200',
        highlighted && 'ring-2 ring-primary-500',
        dimmed && 'opacity-40'
      )}
    >
      {/* Stretched hit area - see the component doc comment above. `title`
          makes the position tooltip reachable from anywhere on the card, not
          just the clamped text under the name. */}
      <Link
        to={employeeHref}
        aria-hidden="true"
        tabIndex={-1}
        title={node.position}
        className="absolute inset-0 z-0 cursor-pointer"
      />

      {isMe && (
        <span
          data-testid="org-node-you"
          className="absolute -top-2.5 right-3 rounded-full bg-warning-500 px-2 py-0.5 text-[10px]
            font-bold uppercase tracking-wide text-white"
        >
          You
        </span>
      )}
      <div className="flex items-center gap-2.5">
        <div
          className={twMerge(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white',
            colors.avatar
          )}
        >
          {initialsOf(node.first_name, node.last_name)}
        </div>
        <div className="min-w-0">
          <Link
            to={employeeHref}
            className="relative z-10 block truncate rounded-sm text-sm font-semibold text-gray-900
              no-underline hover:underline focus-visible:underline focus-visible:outline-none
              focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1"
          >
            {node.first_name} {node.last_name}
          </Link>
          {/* line-clamp-2, not truncate: a one-line ellipsis was cutting off
              longer titles ("Chief Executive Offi..."); title carries the
              full text for a pointer hover, since a clamped line has no
              built-in "show more". Flexbox's default cross-axis alignment
              already keeps every card in a row starting at the same top
              edge regardless of how many lines this wraps to. */}
          <p className="line-clamp-2 text-xs text-gray-500" title={node.position}>
            {node.position}
          </p>
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-1.5">
        <span
          className={twMerge(
            'whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold',
            node.department_name ? colors.pill : 'bg-gray-100 text-gray-500'
          )}
        >
          {node.department_name || 'No department'}
        </span>
        <span className="whitespace-nowrap text-[11px] text-gray-500">
          {/* The API's real count, not node.children.length: in scoped mode
              an ancestor renders only its one spine child, but its true
              direct_report_count (e.g. 3, with 2 more hidden) must still show. */}
          {node.direct_report_count > 0 ? `${node.direct_report_count} direct` : '-'}
        </span>
      </div>

      {node.hidden_report_count > 0 && (
        <p
          data-testid={`org-hidden-count-${node.id}`}
          className="mt-1.5 text-[11px] italic text-gray-400"
        >
          {node.hidden_report_count} other report{node.hidden_report_count === 1 ? '' : 's'} not shown
        </p>
      )}

      {hasChildren && (
        <button
          type="button"
          onClick={handleToggleClick}
          onKeyDown={handleToggleKeyDown}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand reports' : 'Collapse reports'}
          data-testid={`org-toggle-${node.id}`}
          className="absolute -bottom-3 left-1/2 z-10 flex h-6 w-6 -translate-x-1/2 items-center justify-center
            rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-primary-600
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        >
          {isCollapsed ? <Plus size={13} /> : <Minus size={13} />}
        </button>
      )}
    </div>
  );
};

export default OrgNode;
